import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import TechnicalAnalysisService from "../services/TechnicalAnalysisService";

// Icons
import ShowChartIcon from "@mui/icons-material/ShowChart";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import HistoryIcon from "@mui/icons-material/History";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

const timeframes = [
  { value: "1m", label: "1 Minute" },
  { value: "5m", label: "5 Minutes" },
  { value: "15m", label: "15 Minutes" },
  { value: "1h", label: "1 Hour" },
  { value: "4h", label: "4 Hours" },
  { value: "1d", label: "1 Day" },
  { value: "1w", label: "1 Week" },
];

const indicators = [
  { value: "rsi", label: "RSI" },
  { value: "macd", label: "MACD" },
  { value: "atr", label: "ATR" },
  { value: "bollinger", label: "Bollinger Bands" },
  { value: "ema", label: "EMA" },
  { value: "sma", label: "SMA" },
  { value: "ichimoku", label: "Ichimoku Cloud" },
];

function TechnicalAnalysis() {
  // Analysis Parameters
  const [symbol, setSymbol] = useState("BTC/USD");
  const [timeframe, setTimeframe] = useState("1d");
  const [selectedIndicators, setSelectedIndicators] = useState([
    "rsi",
    "macd",
    "atr",
    "bollinger",
  ]);
  const [availableIndicators, setAvailableIndicators] = useState({
    standard: [],
    advanced: [],
  });

  // Analysis Results
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);

  // Technical Node Status
  const [nodeStatus, setNodeStatus] = useState({ status: "unknown" });

  // Strategy History
  const [activeTab, setActiveTab] = useState(0); // 0 = analysis, 1 = history
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [strategyHistory, setStrategyHistory] = useState(null);
  const [selectedStrategyId, setSelectedStrategyId] = useState("all");
  const [timeRange, setTimeRange] = useState({ start: null, end: null });
  const [activeStrategies, setActiveStrategies] = useState([]);

  // Load initial data on component mount
  useEffect(() => {
    // Fetch technical node status
    const fetchNodeStatus = async () => {
      try {
        const status = await TechnicalAnalysisService.getTechnicalNodeStatus();
        setNodeStatus(status);
      } catch (err) {
        console.error("Failed to fetch technical node status:", err);
        setNodeStatus({ status: "error", error: err.message });
      }
    };

    // Fetch available indicators
    const fetchIndicators = async () => {
      try {
        const indicators =
          await TechnicalAnalysisService.getAvailableIndicators();
        setAvailableIndicators(indicators);
      } catch (err) {
        console.error("Failed to fetch available indicators:", err);
      }
    };

    // Fetch active strategies
    const fetchActiveStrategies = async () => {
      try {
        const history =
          await TechnicalAnalysisService.getHistoricalStrategies();
        if (history && history.data) {
          const strategies = history.data.map((item) => ({
            id: item.strategy.id,
            name: item.strategy.name,
            market: item.strategy.market,
          }));
          setActiveStrategies(strategies);
        }
      } catch (err) {
        console.error("Failed to fetch active strategies:", err);
      }
    };

    fetchNodeStatus();
    fetchIndicators();
    fetchActiveStrategies();
  }, []);

  const handleAnalyze = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await TechnicalAnalysisService.analyzeSymbol(
        symbol,
        timeframe,
        selectedIndicators,
      );

      setAnalysis(result);
    } catch (err) {
      console.error("Error analyzing technical data:", err);
      setError(err.message || "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Fetch historical data for strategy
  const fetchStrategyHistory = async () => {
    setHistoryLoading(true);
    setHistoryError(null);

    try {
      // Get strategy ID if not 'all'
      const strategyId =
        selectedStrategyId === "all" ? null : selectedStrategyId;

      // Communicate with technical nodes to get historical strategy data
      const result = await TechnicalAnalysisService.getHistoricalStrategies(
        strategyId,
        timeRange,
      );
      setStrategyHistory(result.data);
    } catch (err) {
      console.error("Error fetching strategy history data:", err);
      setHistoryError("Failed to fetch strategy history. Please try again.");
    } finally {
      setHistoryLoading(false);
    }
  };

  // Handle tab change between Analysis and History
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);

    // If switching to history tab and no data loaded yet, fetch it
    if (newValue === 1 && !strategyHistory) {
      fetchStrategyHistory();
    }
  };

  // Handle strategy selection change
  const handleStrategyChange = (event) => {
    setSelectedStrategyId(event.target.value);
  };

  // Handle date range change
  const handleDateRangeChange = (type, date) => {
    setTimeRange((prev) => ({
      ...prev,
      [type]: date,
    }));
  };

  // Apply filters for strategy history
  const applyHistoryFilters = () => {
    fetchStrategyHistory();
  };

  const getSignalIcon = (signal) => {
    if (!signal) return <TrendingFlatIcon />;

    switch (signal.toLowerCase()) {
      case "buy":
      case "strong buy":
        return <TrendingUpIcon sx={{ color: "success.main" }} />;
      case "sell":
      case "strong sell":
        return <TrendingDownIcon sx={{ color: "error.main" }} />;
      default:
        return <TrendingFlatIcon sx={{ color: "warning.main" }} />;
    }
  };

  const getConfidenceColor = (score) => {
    if (score > 0.7) return "success.main";
    if (score > 0.4) return "warning.main";
    return "error.main";
  };
  return (
    <Box>
      {/* Header with Tabs */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{
          display: "flex", alignItems: "center", mb: 2, p: 2, borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(11,17,32,0.85) 0%, rgba(24,27,33,0.9) 100%)',
          backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}>
          <ShowChartIcon sx={{ fontSize: 28, mr: 1, color: '#00FF94' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: 'white' }}>
            Technical Analysis
          </Typography>
        </Box>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab icon={<AnalyticsIcon />} label="Live Analysis" />
          <Tab icon={<HistoryIcon />} label="Strategy History" />
        </Tabs>
      </Box>

      {/* Analysis Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Analysis Form */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Analysis Parameters
                </Typography>

                <Box
                  component="form"
                  noValidate
                  autoComplete="off"
                  sx={{ mt: 3 }}
                >
                  <TextField
                    fullWidth
                    label="Symbol"
                    variant="outlined"
                    value={symbol}
                    onChange={(e) => setSymbol(e.target.value)}
                    margin="normal"
                  />

                  <FormControl fullWidth margin="normal">
                    <InputLabel>Timeframe</InputLabel>
                    <Select
                      value={timeframe}
                      label="Timeframe"
                      onChange={(e) => setTimeframe(e.target.value)}
                    >
                      {timeframes.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth margin="normal">
                    <InputLabel>Indicators</InputLabel>
                    <Select
                      multiple
                      value={selectedIndicators}
                      label="Indicators"
                      onChange={(e) => setSelectedIndicators(e.target.value)}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => {
                            // Find the corresponding indicator to get its label
                            let label = value;
                            const foundIndicator = indicators.find(
                              (i) => i.value === value,
                            );
                            if (foundIndicator) label = foundIndicator.label;

                            // We must return a string or JSX element, not an object
                            return (
                              <Chip key={value} label={label} size="small" />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {availableIndicators.standard &&
                      availableIndicators.standard.length > 0 ? (
                        <>
                          <Typography
                            variant="caption"
                            sx={{ px: 2, py: 1, display: "block" }}
                          >
                            Standard Indicators
                          </Typography>
                          {availableIndicators.standard.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Tooltip
                                title={option.description || ""}
                                placement="right"
                              >
                                <Box sx={{ display: "flex", width: "100%" }}>
                                  {typeof option.label === "string"
                                    ? option.label
                                    : option.value}
                                </Box>
                              </Tooltip>
                            </MenuItem>
                          ))}
                          <Divider />
                          <Typography
                            variant="caption"
                            sx={{ px: 2, py: 1, display: "block" }}
                          >
                            Advanced Indicators
                          </Typography>
                          {availableIndicators.advanced.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Tooltip
                                title={option.description || ""}
                                placement="right"
                              >
                                <Box sx={{ display: "flex", width: "100%" }}>
                                  {typeof option.label === "string"
                                    ? option.label
                                    : option.value}
                                </Box>
                              </Tooltip>
                            </MenuItem>
                          ))}
                        </>
                      ) : (
                        // Fallback to static list if API indicators not loaded
                        indicators.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {typeof option.label === "string"
                              ? option.label
                              : option.value}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>

                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleAnalyze}
                    disabled={loading}
                    sx={{ mt: 3 }}
                    startIcon={
                      loading ? (
                        <CircularProgress size={24} />
                      ) : (
                        <AnalyticsIcon />
                      )
                    }
                  >
                    {loading ? "Analyzing..." : "Analyze"}
                  </Button>
                </Box>

                {/* Technical Node Status */}
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Technical Analysis Node Status
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: nodeStatus.is_initialized
                          ? "success.main"
                          : "error.main",
                        mr: 1,
                      }}
                    />
                    <Typography variant="body2">
                      {nodeStatus.is_initialized
                        ? "Node Online"
                        : "Node Offline"}
                    </Typography>
                  </Box>
                  {nodeStatus.thread_id && (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        Thread ID: {nodeStatus.thread_id.substring(0, 8)}...
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        Active Monitors: {nodeStatus.active_monitors}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        Signals Today: {nodeStatus.signals_generated_today}
                      </Typography>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Analysis Results */}
          <Grid item xs={12} md={8}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Analysis Results
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                {loading && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", my: 4 }}
                  >
                    <CircularProgress />
                  </Box>
                )}

                {!loading && !error && !analysis && (
                  <Paper
                    sx={{
                      py: 10,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      backgroundColor: "background.paper",
                    }}
                  >
                    <ShowChartIcon
                      sx={{ fontSize: 60, color: "text.secondary", mb: 2 }}
                    />
                    <Typography variant="body1" color="text.secondary">
                      Enter a symbol and timeframe and click 'Analyze' to get
                      technical insights
                    </Typography>
                  </Paper>
                )}

                {!loading && !error && analysis && (
                  <Box>
                    {/* Recommendation Summary */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        mb: 3,
                        backgroundColor: (theme) =>
                          analysis.data.signal?.toLowerCase().includes("buy")
                            ? theme.palette.success.dark + "15"
                            : analysis.data.signal
                                  ?.toLowerCase()
                                  .includes("sell")
                              ? theme.palette.error.dark + "15"
                              : theme.palette.warning.dark + "15",
                        borderRadius: 2,
                      }}
                    >
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              p: 1,
                            }}
                          >
                            {getSignalIcon(analysis.data.signal)}
                          </Box>
                        </Grid>
                        <Grid item xs>
                          <Typography variant="h6" component="div">
                            {analysis.data.signal || "Neutral"} Signal for{" "}
                            {symbol}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {analysis.data.reasoning ||
                              "Based on technical analysis"}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Box
                            sx={{
                              p: 1,
                              textAlign: "center",
                              borderRadius: 1,
                              backgroundColor: "background.default",
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Confidence
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                color: getConfidenceColor(
                                  analysis.confidence_score,
                                ),
                              }}
                            >
                              {Math.round(analysis.confidence_score * 100)}%
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* Indicators Analysis */}
                    <Typography variant="subtitle1" gutterBottom>
                      Technical Indicators
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {analysis.data.indicators &&
                        Object.entries(analysis.data.indicators).map(
                          ([key, value]) => (
                            <Grid item xs={12} sm={6} md={4} key={key}>
                              <Paper
                                sx={{
                                  p: 2,
                                  height: "100%",
                                  borderLeft: "4px solid",
                                  borderColor: "primary.main",
                                }}
                              >
                                <Typography variant="subtitle2" gutterBottom>
                                  {key.toUpperCase()}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Value:{" "}
                                  {typeof value === "object"
                                    ? JSON.stringify(value)
                                    : value}
                                </Typography>
                              </Paper>
                            </Grid>
                          ),
                        )}
                    </Grid>

                    {/* Patterns Detected */}
                    {analysis.data.patterns &&
                      analysis.data.patterns.length > 0 && (
                        <>
                          <Typography variant="subtitle1" gutterBottom>
                            Chart Patterns Detected
                          </Typography>
                          <Grid container spacing={2} sx={{ mb: 3 }}>
                            {analysis.data.patterns.map((pattern, index) => (
                              <Grid item xs={12} sm={6} key={index}>
                                <Paper
                                  sx={{
                                    p: 2,
                                    borderLeft: "4px solid",
                                    borderColor: "secondary.main",
                                  }}
                                >
                                  <Typography variant="subtitle2">
                                    {pattern.name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {pattern.description}
                                  </Typography>
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        </>
                      )}

                    {/* Support and Resistance */}
                    {analysis.data.support_resistance &&
                      analysis.data.support_resistance.length > 0 && (
                        <>
                          <Typography variant="subtitle1" gutterBottom>
                            Support & Resistance Levels
                          </Typography>
                          <Box sx={{ mb: 3 }}>
                            {analysis.data.support_resistance.map(
                              (level, index) => (
                                <Chip
                                  key={index}
                                  label={`${level.type}: ${level.level}`}
                                  color={
                                    level.type.toLowerCase() === "support"
                                      ? "success"
                                      : "error"
                                  }
                                  variant="outlined"
                                  sx={{ mr: 1, mb: 1 }}
                                />
                              ),
                            )}
                          </Box>
                        </>
                      )}

                    {/* Analysis Metadata */}
                    <Divider sx={{ my: 2 }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 2,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Analysis ID: {analysis.analysis_id?.substring(0, 12)}...
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Generated:{" "}
                        {new Date(analysis.timestamp).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Strategy History Tab Content */}
      {activeTab === 1 && (
        <Box>
          {/* Filter Controls */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={3} alignItems="flex-end">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="strategy-select-label">Strategy</InputLabel>
                  <Select
                    labelId="strategy-select-label"
                    value={selectedStrategyId}
                    label="Strategy"
                    onChange={handleStrategyChange}
                  >
                    <MenuItem value="all">All Strategies</MenuItem>
                    {activeStrategies.map((strategy) => (
                      <MenuItem key={strategy.id} value={strategy.id}>
                        {strategy.name} ({strategy.market})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="From Date"
                  type="date"
                  value={timeRange.start || ""}
                  onChange={(e) =>
                    handleDateRangeChange("start", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="To Date"
                  type="date"
                  value={timeRange.end || ""}
                  onChange={(e) => handleDateRangeChange("end", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  variant="contained"
                  onClick={applyHistoryFilters}
                  startIcon={<FilterListIcon />}
                  fullWidth
                >
                  Apply
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* History Content */}
          {historyLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
              <CircularProgress />
              <Typography variant="h6" sx={{ ml: 2 }}>
                Loading strategy history...
              </Typography>
            </Box>
          ) : historyError ? (
            <Alert severity="error" sx={{ mb: 4 }}>
              {historyError}
            </Alert>
          ) : strategyHistory && strategyHistory.length > 0 ? (
            <>
              {/* Strategy Performance Cards */}
              <Typography variant="h6" gutterBottom>
                Strategy Performance
              </Typography>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {strategyHistory.map((stratData) => (
                  <Grid item xs={12} md={6} key={stratData.strategy.id}>
                    <Card>
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 2,
                          }}
                        >
                          <Typography variant="h6">
                            {stratData.strategy.name}
                          </Typography>
                          <Chip
                            size="small"
                            label={stratData.strategy.market}
                            color={
                              stratData.strategy.market === "Crypto"
                                ? "secondary"
                                : stratData.strategy.market === "Stocks"
                                  ? "primary"
                                  : "default"
                            }
                          />
                        </Box>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          {stratData.strategy.description}
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Win Rate
                            </Typography>
                            <Typography variant="h6">
                              {stratData.performance.win_rate}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Total Signals
                            </Typography>
                            <Typography variant="h6">
                              {stratData.performance.total_signals}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Avg Return
                            </Typography>
                            <Typography
                              variant="h6"
                              color={
                                parseFloat(
                                  stratData.performance.average_return,
                                ) > 0
                                  ? "success.main"
                                  : "error.main"
                              }
                            >
                              {stratData.performance.average_return}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Cumulative
                            </Typography>
                            <Typography
                              variant="h6"
                              color={
                                parseFloat(
                                  stratData.performance.cumulative_return,
                                ) > 0
                                  ? "success.main"
                                  : "error.main"
                              }
                            >
                              {stratData.performance.cumulative_return}
                            </Typography>
                          </Grid>
                        </Grid>

                        <Button
                          variant="outlined"
                          fullWidth
                          sx={{ mt: 2 }}
                          onClick={() => {
                            // Could implement a modal with detailed stats here
                            console.log(
                              "View detailed stats for:",
                              stratData.strategy.id,
                            );
                          }}
                        >
                          View Details
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Signal History */}
              <Typography variant="h6" gutterBottom>
                Recent Signals
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Strategy</TableCell>
                      <TableCell>Symbol</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Confidence</TableCell>
                      <TableCell>Result</TableCell>
                      <TableCell>Return</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {strategyHistory.flatMap((stratData) =>
                      stratData.signals.slice(0, 5).map((signal) => (
                        <TableRow key={signal.id} hover>
                          <TableCell>
                            {new Date(signal.date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>{stratData.strategy.name}</TableCell>
                          <TableCell>{signal.symbol}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={signal.action.toUpperCase()}
                              color={
                                signal.action === "buy" ? "success" : "error"
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {Math.round(signal.confidence * 100)}%
                          </TableCell>
                          <TableCell>
                            {signal.result.status === "closed" ? (
                              parseFloat(signal.result.return) > 0 ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    color: "success.main",
                                  }}
                                >
                                  <CheckCircleIcon
                                    fontSize="small"
                                    sx={{ mr: 0.5 }}
                                  />
                                  Win
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    color: "error.main",
                                  }}
                                >
                                  <CancelIcon
                                    fontSize="small"
                                    sx={{ mr: 0.5 }}
                                  />
                                  Loss
                                </Box>
                              )
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: "info.main",
                                }}
                              >
                                <Typography variant="body2">-</Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            {signal.result.status === "closed" ? (
                              <Typography
                                color={
                                  parseFloat(signal.result.return) > 0
                                    ? "success.main"
                                    : "error.main"
                                }
                              >
                                {signal.result.return_pct}
                              </Typography>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={signal.result.status.toUpperCase()}
                              color={
                                signal.result.status === "active"
                                  ? "primary"
                                  : "default"
                              }
                            />
                          </TableCell>
                        </TableRow>
                      )),
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" gutterBottom>
                No Strategy History Available
              </Typography>
              <Typography variant="body1">
                Unable to retrieve strategy history data.
              </Typography>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={fetchStrategyHistory}
                sx={{ mt: 2 }}
              >
                Retry
              </Button>
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
}

export default TechnicalAnalysis;
